import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
    setPixelId, 
    initializePixel, 
    trackPageView,
    isPixelInitialized
} from '../../lib/tracking';

interface TrackingSettings {
    pixelId: string | null;
    pixelEnabled: boolean;
    enableViewContent: boolean;
    enableAddToCart: boolean;
    enableInitiateCheckout: boolean;
    enablePurchase: boolean;
}

async function fetchTrackingSettings(): Promise<TrackingSettings> {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    console.log('[TrackingProvider] Fetching settings from:', apiUrl);
    const response = await fetch(`${apiUrl}/tracking/public/settings`);
    if (!response.ok) throw new Error('Failed to fetch tracking settings');
    const data = await response.json();
    console.log('[TrackingProvider] Settings received:', data);
    return data;
}

export function TrackingProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();

    // Fetch tracking settings
    const { data: settings, isLoading, error } = useQuery({
        queryKey: ['tracking-settings'],
        queryFn: fetchTrackingSettings,
        staleTime: 1000 * 60 * 60, // 1 hour
        retry: 1,
    });

    // Initialize pixel when settings load
    useEffect(() => {
        if (settings?.pixelEnabled && settings?.pixelId) {
            console.log('[TrackingProvider] Pixel enabled, setting ID:', settings.pixelId);
            setPixelId(settings.pixelId);
            // Initialize immediately (consent handled separately if needed)
            initializePixel();
        } else if (settings) {
            console.log('[TrackingProvider] Pixel disabled or no ID:', settings);
        }
    }, [settings?.pixelId, settings?.pixelEnabled]);

    // Track page views on route change
    useEffect(() => {
        if (settings?.pixelEnabled && isPixelInitialized()) {
            // Small delay to ensure pixel is fully loaded
            const timer = setTimeout(() => {
                trackPageView();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, settings?.pixelEnabled]);

    // Log loading/error states
    useEffect(() => {
        if (isLoading) {
            console.log('[TrackingProvider] Loading settings...');
        }
        if (error) {
            console.error('[TrackingProvider] Error loading settings:', error);
        }
    }, [isLoading, error]);

    return <>{children}</>;
}

export { useQuery };
